/**
 * Single source of truth for the API prefix.
 *
 * The backend exposes routes under two prefixes: legacy bare `/api` (incomplete)
 * and canonical `/api/v1` (complete). The frontend must target `/api/v1`
 * EVERYWHERE so the backend can eventually retire bare `/api` without breaking
 * the UI. Centralising the prefix here means a misconfigured `VITE_API_URL` can
 * never silently send traffic to bare `/api` again.
 *
 * `canonicalizeApiBase` normalises every shape `VITE_API_URL` can take:
 *   - `/api/v1`                         → `/api/v1`            (relative, same-origin proxy — the default)
 *   - `https://host/api/v1`             → `https://host/api/v1` (already canonical)
 *   - `https://host/api`                → `https://host/api/v1` (legacy bare → upgraded)
 *   - `https://host`                    → `https://host/api/v1` (host only → prefix appended)
 *
 * Both the HTTP client (`src/api/client.ts`) and the realtime manager
 * (`src/lib/realtime/realtime-manager.ts`) read from here, so the prefix can't
 * drift between REST and WebSocket transports.
 */

const RAW_API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** API version segment. Bump in one place if the backend ever ships `/api/v2`. */
export const API_PREFIX = '/api/v1';

function canonicalizeApiBase(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, '');
  // Already canonical (`/api/v1`, `/api/v2`, …) — leave as-is.
  if (/\/api\/v\d+$/.test(trimmed)) return trimmed;
  // Legacy bare `/api` — upgrade to the canonical prefix.
  if (/\/api$/.test(trimmed)) return trimmed.replace(/\/api$/, API_PREFIX);
  // Host only (or empty) — append the prefix.
  return `${trimmed}${API_PREFIX}`;
}

/**
 * Canonical API base — ALWAYS ends in `/api/v1`, never bare `/api`. Pass this to
 * the SDK's `client.setConfig({ baseUrl })`; all relative SDK/`sdkClient` calls
 * inherit it.
 */
export const API_BASE_URL = canonicalizeApiBase(RAW_API_URL);

/**
 * Bare origin for Socket.IO (which lives at `/socket.io`, OUTSIDE `/api`). When
 * the base is relative (same-origin proxy) there's no host to derive, so fall
 * back to the current window origin.
 */
export function apiOrigin(): string {
  if (API_BASE_URL.startsWith('http')) {
    return API_BASE_URL.replace(/\/api\/v\d+$/, '');
  }
  return window.location.origin;
}

/** Full raw-WebSocket URL (`<origin>/api/v1/ws`) with the `http(s)→ws(s)` swap. */
export function rawWsUrl(token: string): string {
  return `${apiOrigin().replace(/^http/, 'ws')}${API_PREFIX}/ws?token=${token}`;
}
